import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import MDBox from "components/MDBox";
import Grid from "@mui/material/Grid";
import { Button, IconButton, useMediaQuery, useTheme } from "@mui/material";
import useStyles from "layouts/pages/applicants/components/ApplicantOnboardingPanel/Attachments/styles";
import updateApplicant from "layouts/pages/applicants/actions/updateApplicant";
import { baseAxios } from "config";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import { useAppContextController } from "context/AppContext";
import { useNewApplicantContext } from "context/NewApplicantContext";
import { useSnackbar } from "notistack";
import { useSSE } from "hooks/useSSE";
import { getCompanyImageUrl } from "utils/helpers/upload";
import { getRequiredEmptyBoxes } from "utils/attachmentHelpers";
import FileViewer from "./FileViewer";

const Attachments = ({ currentApplicant, setOpen, attachmentTitle, noButton = false }) => {
  const {
    company,
    currentApplicant: existingApplicant,
    setCurrentApplicant,
    user,
    currentLoggedUser,
  } = useAppContextController();
  const { setApplicant } = useNewApplicantContext();
  const {
    data: SSEData,
    error: SSEError,
    isConnected,
  } = useSSE(`${baseAxios.defaults.baseURL}/applicants/ocr/updates`, !user);

  useEffect(() => {
    console.log("data ", SSEData, "error ", SSEError, "isConnected ", isConnected);
  }, [SSEData, SSEError, isConnected]);

  useEffect(() => {
    if (
      currentApplicant &&
      SSEData &&
      currentApplicant._id === SSEData.applicantId &&
      SSEData.attachments?.length > 0
    ) {
      setCurrentApplicant({ ...currentApplicant, attachments: SSEData.attachments });
    }
  }, [SSEData]);

  const isUser = currentLoggedUser?.userType === "User";
  const isAdmin =
    currentLoggedUser?.userType === "Admin" || currentLoggedUser?.userType === "Master";
  const IMAGE_SERVER = getCompanyImageUrl(company);
  const styles = useStyles();
  const [files, setFiles] = useState([]);
  const [deletePolicyErr, setDeletePolicyErr] = useState(false);
  const [toastAlert, setToastAlert] = useState({
    isShow: false,
    message: "",
  });
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const size = isMobile ? 70 : 100;

  const queryClient = useQueryClient();
  const uploadUrl = `/upload/${currentApplicant?.slug}/venues/banner`;

  const filesFinal = useMemo(() => {
    if (!files || isAdmin) return files;
    return files.filter((f) => f.hidden !== "Yes");
  }, [files, isAdmin]);

  const saveUpdate = async (values) => {
    // if (!currentApplicant._id) {
    //   setToastAlert({ isShow: true, message: "Something went wrong!", status: "error" });
    //   return;
    // }
    // await updateVenueMutation.mutateAsync({
    //   venueId: currentApplicant._id,
    //   data: values,
    // });
  };

  const renameFileMutation = useMutation(
    async ({ rename, original }) => {
      // Construct an absolute URL using the base URL from your axios config
      const baseUrl = baseAxios.defaults.baseURL;
      const url = new URL(
        `/outside-protected/upload/rename/${currentApplicant?.slug}/venues/other`,
        baseUrl
      );

      // Add search parameters
      url.searchParams.append("rename", rename);
      url.searchParams.append("original", original);

      // Make the patch request
      await baseAxios.patch(url.toString());
    },
    {
      onError: (error) =>
        setToastAlert({ isShow: true, message: error.toString(), status: "error" }),
      onSuccess: () => {
        setToastAlert({ isShow: true, message: "Successfully renamed", status: "success" });
      },
    }
  );

  const updateApplicantMutation = useMutation(updateApplicant, {
    onError: () => enqueueSnackbar("Something went wrong!", { variant: "error" }),
    onSuccess: async (_, { data }) => {
      await queryClient.invalidateQueries("applicants");
      if (existingApplicant) setCurrentApplicant({ ...currentApplicant, ...data });
      else if (currentApplicant) setApplicant({ ...currentApplicant, ...data });
      setFiles(data?.attachments);
      enqueueSnackbar("Attachments updated!", { variant: "success" });
    },
  });

  const addNew = (e) => {
    setOpen(true);
  };

  const deleteFileHandler = async (e, idx) => {
    if (existingApplicant || currentApplicant) {
      // delete from DB
      const tempAttachments = [...files];
      const updatedAttachments = tempAttachments.filter((_, i) => i !== idx);

      await updateApplicantMutation.mutateAsync({
        applicantId: existingApplicant?._id || currentApplicant?._id,
        data: {
          attachments: updatedAttachments,
        },
        outsideMode: user ? "" : "protected",
      });
    }
  };

  const editFileNameHandler = (e, idx) => {
    const newFiles = [...files];

    // get the image extension
    const imgExt = newFiles[idx].name?.substring(newFiles[idx].name.lastIndexOf("."));

    // check if edited
    const removeExt = newFiles[idx].name?.substring(0, newFiles[idx].name.lastIndexOf("."));
    newFiles[idx].isEdited = removeExt !== e.target.value;

    // set the edited name value
    newFiles[idx].editedName = newFiles[idx].isEdited
      ? e.target.value + imgExt
      : newFiles[idx].name;

    // set the nAttachmenttate
    setFiles(newFiles);
  };

  const confirmFileNameChanges = async (e, idx) => {
    await renameFileMutation.mutateAsync({
      original: files[idx].name,
      rename: files[idx].editedName,
    });

    const newFiles = [...files];
    newFiles[idx] = { ...newFiles[idx], name: newFiles[idx].editedName, isEdited: false };
    setFiles(newFiles);

    await updateApplicant.mutateAsync({
      applicantId: currentApplicant?._id,
      data: { attachments: newFiles.map((img) => img.name) },
    });
  };

  const cancelFileNameChanges = useCallback(
    (e, idx) => {
      const newFiles = [...files];
      const originalName = newFiles[idx].name;
      newFiles[idx] = { ...newFiles[idx], editedName: originalName, isEdited: false };
      setFiles(newFiles);
    },
    [files]
  );

  useEffect(() => {
    if (currentApplicant?.attachments?.length) {
      setFiles(
        currentApplicant?.attachments.map((file) => {
          const {
            docType,
            filename,
            name,
            title,
            type,
            uploadDate,
            presignedUrl,
            recognition,
            recognition_date: recognitionDate,
            hidden,
          } = file;
          return {
            docType,
            name: filename || name,
            title,
            type,
            presignedUrl,
            recognition,
            recognitionDate,
            hidden,
            ...file,
          };
        })
      );
    } else {
      setFiles([]);
    }
  }, [currentApplicant?.attachments]);

  // Get required empty boxes using the utility function
  const requiredEmptyBoxes = useMemo(() => getRequiredEmptyBoxes(filesFinal), [filesFinal]);

  return (
    <MDBox className={styles.otherSection}>
      <Grid container>
        <Grid item lg={10} xs={12} display="flex" alignItems="center">
          {attachmentTitle}
        </Grid>
        {!noButton && (
          <Grid item lg={2} xs={12} display="flex" justifyContent="flex-start">
            {!isMobile ? (
              <IconButton className={styles.addButton} color="info" onClick={addNew}>
                <AddCircleRoundedIcon fontSize="medium" />
              </IconButton>
            ) : (
              <Button
                variant="contained"
                fullWidth
                sx={{ borderRadius: "5px", mb: 1 }}
                onClick={addNew}
              >
                Upload File
              </Button>
            )}
          </Grid>
        )}
      </Grid>

      <MDBox className={styles.section_images_wapper}>
        <Grid
          container
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent={isMobile ? "center" : "flex-start"}
        >
          {filesFinal.map((file, idx) => {
            return (
              <Grid item className={styles.section_image_container} key={file.name}>
                <FileViewer
                  file={file}
                  currentApplicant={currentApplicant}
                  IMAGE_SERVER={IMAGE_SERVER}
                  company={company}
                  size={size}
                  onDelete={(e) => deleteFileHandler(e, idx)}
                  isUser={isUser}
                  currentLoggedUser={currentLoggedUser}
                  cancelFileNameChanges={(e) => cancelFileNameChanges(e, idx)}
                  confirmFileNameChanges={(e) => confirmFileNameChanges(e, idx)}
                  editFileNameHandler={(e) => editFileNameHandler(e, idx)}
                />
              </Grid>
            );
          })}

          {/* Render empty boxes for required documents */}
          {requiredEmptyBoxes?.map((emptyBox, index) => (
            <React.Fragment
              key={`empty-${emptyBox.type}-${emptyBox.description.replace(/\s+/g, "-")}`}
            >
              <Grid item className={styles.section_image_container}>
                <MDBox
                  sx={{
                    width: size + 50,
                    height: size + 85,
                    border: "2px dashed",
                    borderColor: "error.main",
                    borderRadius: "8px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: "#f9f9f9",
                    cursor: "pointer",
                    padding: "8px",
                    marginBottom: "30px",
                    "&:hover": {
                      borderColor: "#999",
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                  onClick={addNew}
                >
                  <AddCircleRoundedIcon sx={{ fontSize: 22, color: "#999", mb: 0.5 }} />
                  <MDBox
                    sx={{
                      fontSize: "12px",
                      fontWeight: "bold",
                      textAlign: "center",
                      color: "error.main",
                      px: 0.5,
                      lineHeight: 1.2,
                      wordWrap: "break-word",
                      overflow: "visible",
                      display: "block",
                      maxHeight: "70px",
                      flex: 1,
                      width: "100%",
                      minHeight: 0,
                      whiteSpace: "normal",
                    }}
                  >
                    {emptyBox.description}
                  </MDBox>
                </MDBox>
              </Grid>
              {index < requiredEmptyBoxes.length - 1 && (
                <Grid
                  item
                  sx={{ display: "flex", alignItems: "center", justifyContent: "center", my: 1 }}
                >
                  <MDBox
                    sx={{
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "#666",
                      px: 1,
                    }}
                  >
                    OR
                  </MDBox>
                </Grid>
              )}
            </React.Fragment>
          ))}
        </Grid>
      </MDBox>
    </MDBox>
  );
};

export default Attachments;
